/**
 * Categorize a sense's raw wiktextract `tags` array and route each tag to the
 * slot it belongs in.
 *
 * `Sense.tags` is wiktextract's flat, multi-categorical grab-bag, passed through
 * verbatim by the extractor (`tools/extract_definitions.py`) and served untouched
 * by the server. It mixes grammar (`transitive`, `uncountable`), register
 * (`slang`, `archaic`), regional (`US`, `Scotland`), structural (`form-of`),
 * hedges (`usually`), and a long uncategorized tail (`Internet`, `slur`). Joining
 * the whole array into one badge renders wrong content — grammatical machinery in
 * a register label, and `form-of`/`alt-of` duplicated against the crossRef rows.
 *
 * Routing (first match wins), preserving source order and de-duplicating:
 *   1. form-of / alt-of  → dropped (already rendered as the crossRef "Form of / Also" rows)
 *   2. hedge             → dropped (the only genuine, disposable information loss)
 *   3. grammar           → grammatical annotation slot
 *   4. regional          → place-label slot (styled distinctly)
 *   5. everything else   → register slot; register + domain + unknown tail, all kept
 *
 * The register slot is the catch-all: register, subject domains, and the unknown
 * tail all land there so nothing (e.g. `slur`, `Internet`, `Judaism`) is silently
 * dropped. A regional tag not in the curated set below simply falls through to it
 * — shown as a register label rather than a place label, never dropped.
 */

/** Suppressed as duplicates of the sense's crossRef ("Form of" / "Also") rows. */
const STRUCTURAL_DUP = new Set(['form-of', 'alt-of']);

/** Suppressed: qualifiers that read as noise in a label. The only true drop. */
const HEDGE = new Set([
  'usually',
  'often',
  'sometimes',
  'broadly',
  'especially',
  'specifically',
  'chiefly',
  'mainly',
  'mostly',
  'generally',
  'typically',
  'by-extension',
  'loosely',
  'also',
  'mildly',
]);

/**
 * Grammatical-category tags — countability, comparability, valency, number,
 * person, tense/aspect, mood, case. Curated for the common tags; an un-listed
 * grammar tag simply falls through to `register` (shown, never silent-dropped).
 */
const GRAMMAR = new Set([
  'uncountable',
  'countable',
  'not-comparable',
  'comparable',
  'transitive',
  'intransitive',
  'ambitransitive',
  'ditransitive',
  'reflexive',
  'impersonal',
  'participle',
  'present',
  'past',
  'gerund',
  'infinitive',
  'imperative',
  'indicative',
  'subjunctive',
  'singular',
  'plural',
  'plural-only',
  'no-plural',
  'in-plural',
  'plural-normally',
  'singular-only',
  'comparative',
  'superlative',
  'first-person',
  'second-person',
  'third-person',
  'attributive',
  'predicative',
  'relational',
  'nominative',
  'objective',
  'genitive',
  'masculine',
  'feminine',
  'neuter',
  'auxiliary',
  'modal',
  'stative',
]);

/**
 * Place/dialect labels. Curated for the common tags; an un-listed region falls
 * through to `register` (shown as a plain label rather than a place label).
 */
const REGIONAL = new Set([
  'US',
  'UK',
  'GB',
  'British',
  'American',
  'England',
  'English',
  'Scotland',
  'Scottish',
  'Wales',
  'Welsh',
  'Ireland',
  'Irish',
  'Australia',
  'Australian',
  'Canada',
  'Canadian',
  'New-Zealand',
  'South-Africa',
  'India',
  'Pakistan',
  'Philippines',
  'Singapore',
  'Malaysia',
  'Nigeria',
  'Hong-Kong',
  'Jamaica',
  'Commonwealth',
  'Northern-England',
  'Southern-US',
  'New-England',
  'Midlands',
  'East-Anglia',
  'Multicultural-London-English',
  'Geordie',
  'Yorkshire',
  'Cockney',
  'Singlish',
  'Ulster',
  'South-Asia',
  'Southern-African',
]);

/** Display form: hyphenated tags read as phrases (`not-comparable` → `not comparable`). */
function humanize(tag: string): string {
  return tag.replace(/-/g, ' ');
}

export interface RoutedSenseTags {
  /** Grammatical annotation (transitive, uncountable, …). */
  readonly grammar: readonly string[];
  /** Register + domain + unknown tail — every survivor kept. The catch-all slot. */
  readonly register: readonly string[];
  /** Place/dialect labels (US, Scotland, …). */
  readonly regional: readonly string[];
}

export function routeSenseTags(tags: readonly string[] | undefined): RoutedSenseTags {
  const grammar: string[] = [];
  const register: string[] = [];
  const regional: string[] = [];
  const seen = new Set<string>();

  for (const raw of tags ?? []) {
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    if (STRUCTURAL_DUP.has(raw) || HEDGE.has(raw)) continue;
    if (GRAMMAR.has(raw)) grammar.push(humanize(raw));
    else if (REGIONAL.has(raw)) regional.push(humanize(raw));
    else register.push(humanize(raw));
  }

  return { grammar, register, regional };
}
